import React, { useState, useMemo, useEffect } from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import { toast } from 'react-toastify';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import Swal from 'sweetalert2';
import moment from 'moment';
import useStore from '../../states/store';
import TabPanel from '../../components/TabPanel';
import Filter from './components/Filter';
import Table from './components/Table';
import AddProductModal from './components/AddProductModal';
import UpdateProductModal from './components/UpdateProductModal';
import { DashboardProvider } from '../Dashboard/useDashboard';
import {
  postInventoryProductsList,
  postInventoryAddProducts,
  postInventoryDeleteProducts,
  postViewProduct,
  postInventoryUpdateProduct,
  getActiveMDProducts,
} from '../../api/productSamplingMaintenanceAPI';

import { getFilterSpecialty } from '../../api/weeklyProductReportApi';
import useProductSamplingInventoryContext from './context/useProductSamplingInventoryContext';

export default function ProductSamplingInventoryMaintenance() {
  const auth = useStore((state) => state.auth);

  const defaultFilter = {
    specialty: [{ label: 'ALL', value: null }],
    date: moment().format('YYYY-MM'),
  };

  const addProductDefault = {
    toggle: false,
    date: moment().format('YYYY-MM'),
    products: [
      {
        id: 1,
        product_code: '',
        product_name: '',
        specialty: '',
        qty: 0,

        product_selected: { value: null, label: 'Select Product' },
        specialty_selected: { value: null, label: 'Select Specialty' },
      },
    ],
  };

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [productsData, setProductsData] = useState([]);
  const [addProductModal, setAddProductModal] = useState(addProductDefault);
  const [updateProductModal, setUpdateProductModal] = useState({
    toggle: false,
    date: moment().format('YYYY-MM'),
    products: [],
  });
  const [filterForm, setFilterForm] = useState(defaultFilter);
  const [clearFilter, setClearFilter] = useState(false);
  const [productOptions, setProductOptions] = useState([]);
  const [specialtyOptions, setSpecialtyOptions] = useState([]);

  const handleChangeActiveTab = (event, newValue) => {
    setActiveTab(newValue);
  };

  const loadProductionOptions = async () => {
    try {
      const response = await getActiveMDProducts({ keyword: '' });
      const { code, data } = response.data;

      if (code === '200') {
        const newArr = data?.map((row, index) => ({
          id: index,
          product_code: row.product_code,
          product_name: row.product_name,
        }));
        const dropdownValue = newArr?.map((row) => ({
          value: row.product_code,
          label: row.product_name,
        }));

        const options = [{ value: null, label: 'Select Product' }, ...dropdownValue];
        setProductOptions(options);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const loadSpecialtyOptions = async () => {
    const response = await getFilterSpecialty();
    const { code, data } = response.data;
    if (code === '200') {
      const dropdownValue = data
        ?.filter((row) => row.specialty !== '')
        .map((row) => ({
          value: row.specialty,
          label: row.specialty,
        }));

      const options = [{ value: null, label: 'Select Specialty' }, ...dropdownValue];
      setSpecialtyOptions(options);
    }
  };

  const searchProductList = async () => {
    try {
      setClearFilter(false);
      setLoading(true);

      const request = {
        specialty:
          filterForm?.specialty.filter((item) => item.value === null).length > 0
            ? []
            : filterForm?.specialty.map((row) => row.value).sort(),
        month: moment(filterForm.date).format('M'),
        year: moment(filterForm.date).format('YYYY'),
      };
      const response = await postInventoryProductsList(request);
      const { code, data } = response.data;

      if (code === '200') {
        setProductsData(data);
      }

      setLoading(false);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const clearProductList = () => {
    setFilterForm(defaultFilter);
    setClearFilter(true);
  };

  const handleAddProductModal = () => {
    setAddProductModal({ ...addProductModal, toggle: true });
  };

  const handleAddProduct = () => {
    try {
      Swal.fire({
        icon: 'warning',
        title: `Are you sure you want to add product(s)`,
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: '<small style="color:#fff"> Confirm </small>',
        cancelButtonText: '<small style="color:#fff"> Cancel </small>',
      }).then((response) => {
        if (response.isConfirmed) {
          const request = {
            datas: addProductModal?.products?.map((row) => ({
              month: moment(addProductModal.date).format('MM'),
              year: moment(addProductModal.date).format('YYYY'),
              specialty: row.specialty,
              product_code: row.product_code,
              product_name: row.product_name,
              qty: row.qty,
            })),
          };
          postInventoryAddProducts(request).then((res) => {
            const { code, message, result } = res.data;
            if (code === '00003') {
              toast.success(message);
              setAddProductModal(addProductDefault);
              searchProductList();
            } else {
              Swal.fire({
                title: 'Invalid Request',
                html: result?.length < 1 ? result?.join('<br />') : message?.join('<br />'),
                icon: 'warning',
              });
            }

            if (code === '00014') {
              toast.success(message);
              setAddProductModal(addProductDefault);
              searchProductList();

              Swal.fire({
                title: 'Success with Warning',
                html: result?.length < 1 ? result?.join('<br />') : message?.join('<br />'),
                icon: 'warning',
              });
            }
          });
        }
      });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleViewProduct = async (productid) => {
    try {
      const request = {
        id: productid,
      };
      const response = await postViewProduct(request);
      const { code, data } = response.data;

      if (code === '200') {
        const productObj = productOptions.filter((row) => row.label === data.product_name);
        const specialtyObj = specialtyOptions.filter((row) => row.value === data.specialty);
        const productArr = [
          {
            id: data.id,
            product_code: data.product_code,
            product_name: data.product_name,
            specialty: data.specialty,
            qty: data.qty,
            product_selected: productObj[0],
            specialty_selected: specialtyObj[0],
          },
        ];

        setUpdateProductModal({
          ...updateProductModal,
          toggle: true,
          date: moment(`${data.year}-${data.month}`).format('YYYY-MM'),
          products: productArr,
        });
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUpdateProduct = () => {
    try {
      Swal.fire({
        icon: 'warning',
        title: `Are you sure you want to update product(s)`,
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: '<small style="color:#fff"> Confirm </small>',
        cancelButtonText: '<small style="color:#fff"> Cancel </small>',
      }).then((response) => {
        if (response.isConfirmed) {
          const request = {
            datas: updateProductModal?.products?.map((row) => ({
              id: row.id,
              month: moment(updateProductModal.date).format('MM'),
              year: moment(updateProductModal.date).format('YYYY'),
              specialty: row.specialty,
              product_code: row.product_code,
              product_name: row.product_name,
              qty: row.qty,
            })),
          };
          postInventoryUpdateProduct(request).then((res) => {
            const { code, message, result } = res.data;
            if (code === '00004') {
              toast.success(message);
              setUpdateProductModal({
                toggle: false,
                date: moment().format('YYYY-MM'),
                products: [],
              });
              searchProductList();
            } else {
              Swal.fire({
                title: 'Invalid Request',
                html: result?.length < 1 ? result?.join('<br />') : message?.join('<br />'),
                icon: 'warning',
              });
            }

            if (code === '00015') {
              toast.success(message);
              setUpdateProductModal({
                toggle: false,
                date: moment().format('YYYY-MM'),
                products: [],
              });
              searchProductList();

              Swal.fire({
                title: 'Success with Warning',
                html: result?.length < 1 ? result?.join('<br />') : message?.join('<br />'),
                icon: 'warning',
              });
            }
          });
        }
      });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteProduct = (id) => {
    try {
      Swal.fire({
        icon: 'warning',
        title: `Are you sure you want to delete product(s)`,
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: '<small style="color:#fff"> Confirm </small>',
        cancelButtonText: '<small style="color:#fff"> Cancel </small>',
      }).then((response) => {
        if (response.isConfirmed) {
          const request = {
            ids: [id],
          };
          postInventoryDeleteProducts(request).then((res) => {
            const { code, message } = res.data;
            if (code === '200') {
              toast.success(message);
              searchProductList();
            } else {
              Swal.fire({
                title: 'Invalid Request',
                html: message?.join('<br />'),
                icon: 'warning',
              });
            }
          });
        }
      });
    } catch (error) {
      toast.error(error.message);
    }
  };

  useEffect(() => {
    searchProductList();
  }, [activeTab, clearFilter]);

  useEffect(() => {
    loadProductionOptions();
    loadSpecialtyOptions();
  }, []);

  return (
    <DashboardProvider>
      <useProductSamplingInventoryContext.Provider
        value={useMemo(
          () => ({
            auth,
            activeTab,
            loading,
            filterForm,
            productsData,
            clearFilter,
            addProductModal,
            updateProductModal,
            productOptions,
            specialtyOptions,
            setFilterForm,
            setAddProductModal,
            searchProductList,
            clearProductList,
            handleAddProductModal,
            handleAddProduct,
            handleDeleteProduct,
            handleViewProduct,
            handleUpdateProduct,
            setUpdateProductModal,
          }),
          [activeTab, filterForm, productsData, addProductModal, updateProductModal],
        )}
      >
        <Box sx={{ backgroundColor: '#fff', p: 1, boxShadow: 3 }}>
          <Filter />
        </Box>
        <Box padding={4}>
          <Box>
            <Box>
              <Grid container spacing={2}>
                <Grid xs={12} sm={12} md={12}>
                  <Box sx={{ backgroundColor: '#fff' }} mt={4}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <Tabs
                        value={activeTab}
                        onChange={handleChangeActiveTab}
                        aria-label='basic tabs example'
                      >
                        <Tab
                          label='Product Sampling Inventory List'
                          id='products-inventory-list'
                          aria-controls='products-inventory-list'
                        />
                      </Tabs>
                    </Box>
                    <TabPanel value={activeTab} index={activeTab}>
                      <Table />
                    </TabPanel>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
        <AddProductModal />
        <UpdateProductModal />
      </useProductSamplingInventoryContext.Provider>
    </DashboardProvider>
  );
}
